import { useState, useEffect, useCallback } from 'react';

const usePersistentState = (storageKey, initialState = {}) => {
  // Helper functions for localStorage
  const saveToStorage = useCallback((key, data) => {
    try {
      const dataWithTimestamp = {
        data: data,
        timestamp: Date.now(),
        expiresIn: 60 * 60 * 1000 // 1 hour in milliseconds
      };
      localStorage.setItem(key, JSON.stringify(dataWithTimestamp));
    } catch (error) {
      console.error('Failed to save to localStorage:', error);
    }
  }, []);

  const loadFromStorage = useCallback((key, defaultValue = null) => {
    try {
      const stored = localStorage.getItem(key);
      if (!stored) {
        return defaultValue;
      }

      const parsedData = JSON.parse(stored);
      
      // Check if this is the new format with timestamp
      if (parsedData && typeof parsedData === 'object' && parsedData.timestamp && parsedData.expiresIn) {
        const now = Date.now();
        const expirationTime = parsedData.timestamp + parsedData.expiresIn;
        
        if (now > expirationTime) {
          // Data has expired, remove it and return default
          // console.log('Stored data expired, clearing:', key);
          localStorage.removeItem(key);
          return defaultValue;
        }
        
        // Data is still valid, return the actual data
        return parsedData.data;
      } else {
        // Old format without timestamp, treat as expired for migration
        // console.log('Old format data found, clearing:', key);
        localStorage.removeItem(key);
        return defaultValue;
      }
    } catch (error) {
      console.error('Failed to load from localStorage:', error);
      return defaultValue;
    }
  }, []);

  const removeFromStorage = useCallback((key) => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Failed to remove from localStorage:', error);
    }
  }, []);

  // Load persisted data or use initial state
  const loadPersistedData = useCallback(() => {
    const persistedData = loadFromStorage(storageKey, null);
    // Merge persisted data with initial state to ensure all properties exist
    return persistedData ? { ...initialState, ...persistedData } : initialState;
  }, [storageKey, initialState, loadFromStorage]);

  // Initialize state with persisted data
  const [state, setState] = useState(() => loadPersistedData());
  const [hasPersistedData, setHasPersistedData] = useState(false);

  // Check if there's cached data on mount
  useEffect(() => {
    const stored = loadFromStorage(storageKey);
    const hasData = stored && (
      (stored.campaignData && Object.keys(stored.campaignData).length > 0) ||
      (stored.selectedPOCs && stored.selectedPOCs.length > 0) ||
      (stored.creativeData && Object.keys(stored.creativeData).length > 0)
    );
    setHasPersistedData(!!hasData);
  }, [storageKey, loadFromStorage]);

  // Persist data whenever state changes
  useEffect(() => {
    saveToStorage(storageKey, state);
  }, [state, storageKey, saveToStorage]);

  // Update specific parts of state
  const updateState = useCallback((updates) => {
    if (typeof updates === 'function') {
      // Handle functional updates
      setState(updates);
    } else {
      // Handle direct object updates
      setState(prevState => ({
        ...prevState,
        ...updates
      }));
    }
  }, []);

  // Clear all persisted data
  const clearPersistedData = useCallback(() => {
    // console.log('Clearing persisted data for:', storageKey);
    setState(initialState);
    removeFromStorage(storageKey);
    setHasPersistedData(false);
  }, [storageKey, initialState, removeFromStorage]);

  // Check if there's any data to clear
  const hasDataToConfirm = (
    (state.campaignData && Object.keys(state.campaignData).length > 0) ||
    (state.selectedPOCs && state.selectedPOCs.length > 0) ||
    (state.creativeData && Object.keys(state.creativeData).length > 0)
  );

  // Get remaining time for persistence
  const getRemainingTime = useCallback(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (!stored) return 0;
      
      const parsedData = JSON.parse(stored);
      if (parsedData && parsedData.timestamp && parsedData.expiresIn) {
        const now = Date.now();
        const expirationTime = parsedData.timestamp + parsedData.expiresIn;
        return Math.max(0, expirationTime - now);
      }
    } catch (error) {
      console.error('Failed to get remaining time:', error);
    }
    return 0;
  }, [storageKey]);

  // Auto-cleanup expired data periodically
  useEffect(() => {
    const checkExpiration = () => {
      const remaining = getRemainingTime();
      if (remaining === 0 && hasDataToConfirm) {
        // console.log('Auto-clearing expired campaign data');
        clearPersistedData();
      }
    };

    // Check every 5 minutes
    const interval = setInterval(checkExpiration, 5 * 60 * 1000);
    
    // Initial check
    checkExpiration();

    return () => clearInterval(interval);
  }, [getRemainingTime, hasDataToConfirm, clearPersistedData]);

  return {
    // State
    state,
    setState,
    hasPersistedData,
    hasDataToConfirm,
    
    // Actions
    updateState,
    clearPersistedData,
    
    // Time utilities
    getRemainingTime,
    
    // Storage utilities (if needed for advanced use cases)
    saveToStorage: (data) => saveToStorage(storageKey, data),
    loadFromStorage: () => loadFromStorage(storageKey),
    removeFromStorage: () => removeFromStorage(storageKey)
  };
};

export default usePersistentState;
